// File: src/components/admin/AuditLogDetailView.js
// Copyright (c) 2025 DefoAI UG (haftungsbeschränkt)

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Button,
    Chip,
    Divider,
    Grid,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    CardHeader,
    Breadcrumbs,
    Link,
    Stack,
    Tabs,
    Tab
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    Person as UserIcon,
    Schedule as TimeIcon,
    Code as CodeIcon,
    Psychology as AiIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    Speed as DurationIcon,
    DeviceHub as SystemIcon
} from '@mui/icons-material';
import { adminApi, handleAdminApiError } from '../../services/adminApi';

const AuditLogDetailView = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [log, setLog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);

    useEffect(() => {
        const fetchLog = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await adminApi.getAuditLog(id);
                // API returns { log: {...} } wrapper, extract the log
                setLog(data?.log || data);
            } catch (err) {
                setError(handleAdminApiError(err).message);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchLog();
        }
    }, [id]);

    const getStatusIcon = (status) => {
        if (status === 'success') return <SuccessIcon color="success" />;
        return <ErrorIcon color="error" />;
    };

    const getActionLabel = (action) => {
        const labels = {
            'ai_generate_text': 'AI Text Generation',
            'ai_generate_image': 'AI Image Generation',
            'user_sync': 'User Sync',
            'user_delete_account': 'Account Deletion',
            'subscription_granted': 'Subscription Granted',
            'subscription_revoked': 'Subscription Revoked',
            'autopilot_activated': 'Autopilot Activated',
            'autopilot_paused': 'Autopilot Paused',
            'autopilot_resumed': 'Autopilot Resumed',
            'autopilot_config_updated': 'Autopilot Config Updated',
            'autopilot_recommendation_applied': 'Recommendation Applied',
            'autopilot_recommendation_dismissed': 'Recommendation Dismissed',
            'autopilot_cycle_completed': 'Autopilot Cycle Completed',
            'autopilot_entity_mutated': 'Autopilot Entity Mutated',
            'autopilot_auto_revert': 'Autopilot Auto Revert',
        };
        return labels[action] || action;
    };

    const formatTimestamp = (ts) => {
        return new Date(ts).toLocaleString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            timeZoneName: 'short'
        });
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                <Button startIcon={<BackIcon />} onClick={() => navigate('/audit')}>
                    Back to Audit Logs
                </Button>
            </Box>
        );
    }

    if (!log) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="warning">Log not found</Alert>
                <Button startIcon={<BackIcon />} onClick={() => navigate('/audit')} sx={{ mt: 2 }}>
                    Back to Audit Logs
                </Button>
            </Box>
        );
    }

    const isAiAction = log.action?.startsWith('ai_generate') || log.action === 'ai_chat_completion';

    return (
        <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
            {/* Breadcrumbs */}
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link component={RouterLink} to="/audit" underline="hover" color="inherit">
                    Audit Logs
                </Link>
                <Typography color="text.primary">{log.id}</Typography>
            </Breadcrumbs>

            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="h5" fontWeight="bold">
                            {getActionLabel(log.action)}
                        </Typography>
                        <Chip
                            icon={getStatusIcon(log.status || log.meta?.status)}
                            label={(log.status || log.meta?.status || 'success').toUpperCase()}
                            color={log.status === 'success' || log.meta?.status === 'success' ? 'success' : 'error'}
                            size="small"
                        />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" fontFamily="monospace">
                        ID: {log.id}
                    </Typography>
                </Box>
                <Button
                    startIcon={<BackIcon />}
                    onClick={() => navigate('/audit')}
                    variant="outlined"
                >
                    Back to Logs
                </Button>
            </Box>

            {/* Quick Info Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={3}>
                    <Card elevation={0} variant="outlined">
                        <CardContent sx={{ py: 1.5 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <TimeIcon color="action" fontSize="small" />
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Timestamp</Typography>
                                    <Typography variant="body2" fontWeight="medium">
                                        {formatTimestamp(log.timestamp)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Card elevation={0} variant="outlined">
                        <CardContent sx={{ py: 1.5 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <UserIcon color="action" fontSize="small" />
                                <Box>
                                    <Typography variant="caption" color="text.secondary">User</Typography>
                                    <Typography variant="body2" fontWeight="medium" noWrap>
                                        <Link component={RouterLink} to={`/users/${log.uid}/audit`} underline="hover">
                                            {log.email || log.uid}
                                        </Link>
                                    </Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Card elevation={0} variant="outlined">
                        <CardContent sx={{ py: 1.5 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <DurationIcon color="action" fontSize="small" />
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Duration</Typography>
                                    <Typography variant="body2" fontWeight="medium">
                                        {log.meta?.duration_ms ? `${log.meta.duration_ms} ms` : 'N/A'}
                                    </Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Card elevation={0} variant="outlined">
                        <CardContent sx={{ py: 1.5 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <SystemIcon color="action" fontSize="small" />
                                <Box>
                                    <Typography variant="caption" color="text.secondary">IP Address</Typography>
                                    <Typography variant="body2" fontWeight="medium" fontFamily="monospace">
                                        {log.ip || log.meta?.ip || 'Unknown'}
                                    </Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Tabs for content */}
            <Paper elevation={0} variant="outlined" sx={{ mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={(e, v) => setActiveTab(v)}
                    sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
                >
                    {isAiAction && <Tab icon={<AiIcon />} iconPosition="start" label="Conversation" />}
                    <Tab icon={<CodeIcon />} iconPosition="start" label="Full JSON" />
                </Tabs>

                {/* AI Conversation Tab */}
                {isAiAction && activeTab === 0 && (
                    <Box sx={{
                        p: 3,
                        bgcolor: '#f8fafc',
                        minHeight: 400
                    }}>
                        {/* System Context Banner */}
                        {log.details?.systemMessage && (
                            <Box sx={{
                                mb: 3,
                                p: 2,
                                bgcolor: '#e0e7ff',
                                borderRadius: 2,
                                borderLeft: '4px solid #6366f1'
                            }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                    <SystemIcon sx={{ fontSize: 18, color: '#4f46e5' }} />
                                    <Typography variant="subtitle2" sx={{ color: '#4338ca', fontWeight: 600 }}>
                                        System Context
                                    </Typography>
                                </Stack>
                                <Typography variant="body2" sx={{ color: '#3730a3' }}>
                                    {log.details.systemMessage}
                                </Typography>
                            </Box>
                        )}

                        {/* Available Tools Banner */}
                        {log.details?.tools && log.details.tools.length > 0 && (
                            <Box sx={{
                                mb: 3,
                                p: 2,
                                bgcolor: '#fef3c7',
                                borderRadius: 2,
                                borderLeft: '4px solid #f59e0b'
                            }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                    <CodeIcon sx={{ fontSize: 18, color: '#d97706' }} />
                                    <Typography variant="subtitle2" sx={{ color: '#92400e', fontWeight: 600 }}>
                                        Available Tools ({log.details.tools.length})
                                    </Typography>
                                </Stack>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {log.details.tools.map((tool, idx) => (
                                        <Chip
                                            key={idx}
                                            label={tool.function?.name || tool.name || 'unknown'}
                                            size="small"
                                            sx={{
                                                bgcolor: '#fef9c3',
                                                color: '#78350f',
                                                fontFamily: 'monospace',
                                                fontWeight: 500
                                            }}
                                        />
                                    ))}
                                </Stack>
                            </Box>
                        )}

                        {/* Chat Container */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                            {/* Multi-turn Messages Array (for ai_chat_completion) */}
                            {log.details?.messages && log.details.messages.length > 0 ? (
                                log.details.messages.map((msg, index) => {
                                    // 1. System Message (Explicit in array)
                                    if (msg.role === 'system') {
                                        return (
                                            <Box key={index} sx={{
                                                mb: 2,
                                                p: 2,
                                                bgcolor: '#e0e7ff',
                                                borderRadius: 2,
                                                borderLeft: '4px solid #6366f1'
                                            }}>
                                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                                    <SystemIcon sx={{ fontSize: 18, color: '#4f46e5' }} />
                                                    <Typography variant="subtitle2" sx={{ color: '#4338ca', fontWeight: 600 }}>
                                                        System Context
                                                    </Typography>
                                                </Stack>
                                                <Typography variant="body2" sx={{
                                                    color: '#3730a3',
                                                    whiteSpace: 'pre-wrap',
                                                    maxHeight: 200,
                                                    overflow: 'auto'
                                                }}>
                                                    {msg.content}
                                                </Typography>
                                            </Box>
                                        );
                                    }

                                    // 2. User Message
                                    if (msg.role === 'user') {
                                        return (
                                            <Box key={index} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                <Box sx={{
                                                    maxWidth: '85%',
                                                    display: 'flex',
                                                    flexDirection: 'row-reverse',
                                                    gap: 1.5
                                                }}>
                                                    <Box sx={{
                                                        width: 40,
                                                        height: 40,
                                                        borderRadius: '50%',
                                                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0,
                                                        boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
                                                    }}>
                                                        <UserIcon sx={{ color: 'white', fontSize: 22 }} />
                                                    </Box>
                                                    <Box sx={{
                                                        bgcolor: 'white',
                                                        borderRadius: '16px 16px 4px 16px',
                                                        p: 2.5,
                                                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                                        border: '1px solid #e2e8f0'
                                                    }}>
                                                        <Typography variant="caption" sx={{
                                                            color: '#64748b',
                                                            fontWeight: 600,
                                                            display: 'block',
                                                            mb: 1
                                                        }}>
                                                            {log.email || 'User'}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                whiteSpace: 'pre-wrap',
                                                                lineHeight: 1.6,
                                                                color: '#1e293b'
                                                            }}
                                                        >
                                                            {msg.content}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        );
                                    }

                                    // 3. Assistant Message (Text + Tool Calls)
                                    if (msg.role === 'assistant') {
                                        const hasToolCalls = msg.tool_calls && msg.tool_calls.length > 0;

                                        return (
                                            <Box key={index} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                                <Box sx={{
                                                    maxWidth: '90%',
                                                    display: 'flex',
                                                    gap: 1.5
                                                }}>
                                                    <Box sx={{
                                                        width: 40,
                                                        height: 40,
                                                        borderRadius: '50%',
                                                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0,
                                                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                                                    }}>
                                                        <AiIcon sx={{ color: 'white', fontSize: 22 }} />
                                                    </Box>
                                                    <Box sx={{ width: '100%' }}>
                                                        <Box sx={{
                                                            bgcolor: 'white',
                                                            borderRadius: '16px 16px 16px 4px',
                                                            p: 2.5,
                                                            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                                            border: '1px solid #e2e8f0'
                                                        }}>
                                                            <Typography variant="caption" sx={{
                                                                color: '#10b981',
                                                                fontWeight: 600
                                                            }}>
                                                                AI Assistant
                                                            </Typography>

                                                            {/* Text Content */}
                                                            {msg.content && (
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        whiteSpace: 'pre-wrap',
                                                                        lineHeight: 1.6,
                                                                        color: '#1e293b',
                                                                        mt: 1
                                                                    }}
                                                                >
                                                                    {msg.content}
                                                                </Typography>
                                                            )}

                                                            {/* Tool Call Blocks */}
                                                            {hasToolCalls && (
                                                                <Stack spacing={2} sx={{ mt: msg.content ? 2 : 1 }}>
                                                                    {msg.tool_calls.map((tool, tIdx) => (
                                                                        <Box key={tIdx} sx={{
                                                                            border: '1px solid #e2e8f0',
                                                                            borderRadius: 2,
                                                                            overflow: 'hidden'
                                                                        }}>
                                                                            <Box sx={{
                                                                                bgcolor: '#f1f5f9',
                                                                                px: 2,
                                                                                py: 1,
                                                                                borderBottom: '1px solid #e2e8f0',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: 1
                                                                            }}>
                                                                                <CodeIcon sx={{ fontSize: 16, color: '#64748b' }} />
                                                                                <Typography variant="caption" fontFamily="monospace" fontWeight="bold" color="text.primary">
                                                                                    {tool.function?.name || 'unknown_tool'}
                                                                                </Typography>
                                                                                <Chip label="Tool Call" size="small" sx={{
                                                                                    height: 18,
                                                                                    fontSize: '0.65rem',
                                                                                    bgcolor: '#cbd5e1'
                                                                                }} />
                                                                            </Box>
                                                                            <Box sx={{
                                                                                p: 2,
                                                                                bgcolor: '#f8fafc',
                                                                                fontFamily: 'Consolas, Monaco, monospace',
                                                                                fontSize: '0.8rem',
                                                                                color: '#334155',
                                                                                whiteSpace: 'pre-wrap'
                                                                            }}>
                                                                                {tool.function?.arguments}
                                                                            </Box>
                                                                        </Box>
                                                                    ))}
                                                                </Stack>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        );
                                    }

                                    // 4. Tool Output Message
                                    if (msg.role === 'tool') {
                                        return (
                                            <Box key={index} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                                <Box sx={{
                                                    maxWidth: '90%',
                                                    display: 'flex',
                                                    gap: 1.5,
                                                    pl: '52px' // Indent to align with assistant content, excluding avatar
                                                }}>
                                                    <Box sx={{
                                                        width: '100%',
                                                        bgcolor: '#ffffff',
                                                        borderRadius: 2,
                                                        border: '1px dashed #cbd5e1',
                                                        p: 0,
                                                        overflow: 'hidden'
                                                    }}>
                                                        <Box sx={{
                                                            px: 2,
                                                            py: 1,
                                                            bgcolor: '#f1f5f9',
                                                            borderBottom: '1px dashed #cbd5e1',
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center'
                                                        }}>
                                                            <Stack direction="row" spacing={1} alignItems="center">
                                                                <SuccessIcon sx={{ fontSize: 16, color: '#059669' }} />
                                                                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                                                    Tool Output
                                                                </Typography>
                                                            </Stack>
                                                            <Typography variant="caption" fontFamily="monospace" color="text.disabled">
                                                                {msg.tool_call_id}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{
                                                            p: 2,
                                                            maxHeight: 200,
                                                            overflow: 'auto',
                                                            fontFamily: 'Consolas, Monaco, monospace',
                                                            fontSize: '0.8rem',
                                                            color: '#475569'
                                                        }}>
                                                            {msg.content}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        );
                                    }

                                    return null;
                                })
                            ) : (
                                /* Fallback: Single prompt/response for older logs */
                                <>
                                    {/* User Message Bubble */}
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Box sx={{
                                            maxWidth: '85%',
                                            display: 'flex',
                                            flexDirection: 'row-reverse',
                                            gap: 1.5
                                        }}>
                                            <Box sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                                boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
                                            }}>
                                                <UserIcon sx={{ color: 'white', fontSize: 22 }} />
                                            </Box>
                                            <Box sx={{
                                                bgcolor: 'white',
                                                borderRadius: '16px 16px 4px 16px',
                                                p: 2.5,
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                                border: '1px solid #e2e8f0'
                                            }}>
                                                <Typography variant="caption" sx={{
                                                    color: '#64748b',
                                                    fontWeight: 600,
                                                    display: 'block',
                                                    mb: 1
                                                }}>
                                                    {log.email || 'User'}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        whiteSpace: 'pre-wrap',
                                                        lineHeight: 1.6,
                                                        color: '#1e293b'
                                                    }}
                                                >
                                                    {log.details?.prompt || 'No prompt available'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </>
                            )}

                            {/* AI Response Bubble - Only show if NOT using messages array (fallback) OR as final response */}
                            {log.details?.response && !log.details?.messages && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                    <Box sx={{
                                        maxWidth: '90%',
                                        display: 'flex',
                                        gap: 1.5
                                    }}>
                                        {/* Avatar */}
                                        <Box sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: '50%',
                                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                                        }}>
                                            <AiIcon sx={{ color: 'white', fontSize: 22 }} />
                                        </Box>

                                        {/* Message */}
                                        <Box sx={{
                                            bgcolor: 'white',
                                            borderRadius: '16px 16px 16px 4px',
                                            p: 2.5,
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                            border: '1px solid #e2e8f0',
                                            width: '100%'
                                        }}>
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                                                <Typography variant="caption" sx={{
                                                    color: '#10b981',
                                                    fontWeight: 600
                                                }}>
                                                    AI Assistant
                                                </Typography>
                                                {log.details?.model && (
                                                    <Chip
                                                        label={log.details.model}
                                                        size="small"
                                                        sx={{
                                                            height: 20,
                                                            fontSize: '0.7rem',
                                                            bgcolor: '#ecfdf5',
                                                            color: '#047857'
                                                        }}
                                                    />
                                                )}
                                            </Stack>

                                            {/* Render structured response */}
                                            {(() => {
                                                const response = log.details?.response;
                                                if (!response) return (
                                                    <Typography color="text.secondary" fontStyle="italic">
                                                        No response available
                                                    </Typography>
                                                );

                                                // Plain text response
                                                return (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            whiteSpace: 'pre-wrap',
                                                            lineHeight: 1.6,
                                                            color: '#1e293b'
                                                        }}
                                                    >
                                                        {response}
                                                    </Typography>
                                                );
                                            })()}
                                        </Box>
                                    </Box>
                                </Box>
                            )}

                            {/* Tool Calls Made by AI Response */}
                            {log.details?.tool_calls && log.details.tool_calls.length > 0 && (
                                <Box sx={{ mt: 3 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 2, color: '#7c3aed', fontWeight: 600 }}>
                                        🔧 AI Called Tools
                                    </Typography>
                                    <Stack spacing={2}>
                                        {log.details.tool_calls.map((tc, idx) => (
                                            <Box
                                                key={idx}
                                                sx={{
                                                    bgcolor: '#f5f3ff',
                                                    borderRadius: 2,
                                                    p: 2,
                                                    borderLeft: '4px solid #8b5cf6'
                                                }}
                                            >
                                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                                    <CodeIcon sx={{ fontSize: 18, color: '#7c3aed' }} />
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{ fontFamily: 'monospace', color: '#5b21b6' }}
                                                    >
                                                        {tc.function?.name || tc.name || 'Unknown'}
                                                    </Typography>
                                                    <Chip
                                                        label="Tool Call"
                                                        size="small"
                                                        sx={{
                                                            bgcolor: '#8b5cf6',
                                                            color: 'white',
                                                            fontSize: '0.65rem',
                                                            height: 18
                                                        }}
                                                    />
                                                </Stack>
                                                <Box
                                                    sx={{
                                                        bgcolor: '#ede9fe',
                                                        borderRadius: 1,
                                                        p: 1.5,
                                                        fontFamily: 'monospace',
                                                        fontSize: '0.8rem',
                                                        whiteSpace: 'pre-wrap',
                                                        overflow: 'auto',
                                                        maxHeight: 200,
                                                        color: '#4c1d95'
                                                    }}
                                                >
                                                    {(() => {
                                                        try {
                                                            const args = typeof tc.function?.arguments === 'string'
                                                                ? JSON.parse(tc.function.arguments)
                                                                : tc.function?.arguments;
                                                            return JSON.stringify(args, null, 2);
                                                        } catch {
                                                            return tc.function?.arguments || 'No arguments';
                                                        }
                                                    })()}
                                                </Box>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                            )}

                            {/* Performance Stats */}
                            {log.meta?.duration_ms && (
                                <Box sx={{
                                    mt: 3,
                                    pt: 2,
                                    borderTop: '1px solid #e2e8f0',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    gap: 4
                                }}>
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <DurationIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                                        <Typography variant="caption" color="text.secondary">
                                            Response time: <strong>{log.meta.duration_ms}ms</strong>
                                        </Typography>
                                    </Stack>
                                    {log.details?.tokens && (
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <Typography variant="caption" color="text.secondary">
                                                Tokens used: <strong>{log.details.tokens}</strong>
                                            </Typography>
                                        </Stack>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}

                {/* Full JSON Tab */}
                {((!isAiAction && activeTab === 0) || (isAiAction && activeTab === 1)) && (
                    <Box sx={{ p: 3 }}>
                        <Box
                            component="pre"
                            sx={{
                                bgcolor: '#1e1e1e',
                                color: '#d4d4d4',
                                p: 3,
                                borderRadius: 1,
                                overflow: 'auto',
                                fontSize: '0.85rem',
                                fontFamily: 'Consolas, Monaco, monospace',
                                maxHeight: 600
                            }}
                        >
                            {JSON.stringify(log, null, 2)}
                        </Box>
                    </Box>
                )}
            </Paper>

            {/* Metadata Section */}
            <Paper elevation={0} variant="outlined">
                <CardHeader title="Request Metadata" />
                <Divider />
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary">User Agent</Typography>
                            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                {log.userAgent || log.meta?.userAgent || 'Unknown'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Typography variant="caption" color="text.secondary">Created At</Typography>
                            <Typography variant="body2">
                                {log.createdAt ? new Date(log.createdAt).toLocaleString() : 'Unknown'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Typography variant="caption" color="text.secondary">Version</Typography>
                            <Typography variant="body2">
                                {log.version || 1}
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Paper>
        </Box>
    );
};

export default AuditLogDetailView;
